import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

const defaultSchedule = {
  "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [], "PH": [],
};

const weekDays = [
  { value: "0", label: 'Monday' },
  { value: "1", label: 'Tuesday' },
  { value: "2", label: 'Wednesday' },
  { value: "3", label: 'Thursday' },
  { value: "4", label: 'Friday' },
  { value: "5", label: 'Saturday' },
  { value: "6", label: 'Sunday' },
  { value: "PH", label: 'Public Holiday' }
];

const Step2_AddSchedule = ({ clinicId, onBack, onNext, error, setError }) => {
  const [scheduleForm, setScheduleForm] = useState({
    from_date: dayjs().format('YYYY-MM-DD'),
    to_date: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    is_default_schedule: false,
    active: true,
    schedule: { ...defaultSchedule }
  });
  const [overlappingSlots, setOverlappingSlots] = useState({});

  const checkForOverlaps = (dayIndex, slots) => {
    if (!slots || slots.length <= 1) return false;
    const sortedSlots = [...slots].sort((a, b) => a.from.localeCompare(b.from));
    const overlaps = [];
    for (let i = 0; i < sortedSlots.length - 1; i++) {
      const currentSlot = sortedSlots[i];
      const nextSlot = sortedSlots[i + 1];
      if (currentSlot.to > nextSlot.from) {
        const currentIndex = slots.findIndex(s => s.from === currentSlot.from && s.to === currentSlot.to);
        const nextIndex = slots.findIndex(s => s.from === nextSlot.from && s.to === nextSlot.to);
        overlaps.push(currentIndex, nextIndex);
      }
    }
    return overlaps.length > 0 ? [...new Set(overlaps)] : false;
  };

  useEffect(() => {
    const newOverlappingSlots = {};
    for (const dayIndex of Object.keys(scheduleForm.schedule)) {
      const overlappingIndices = checkForOverlaps(dayIndex, scheduleForm.schedule[dayIndex]);
      if (overlappingIndices) newOverlappingSlots[dayIndex] = overlappingIndices;
    }
    setOverlappingSlots(newOverlappingSlots);
  }, [scheduleForm.schedule]);

  const handleFormInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setScheduleForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDefaultScheduleChange = (e) => {
    const isDefault = e.target.checked;
    setScheduleForm(prev => ({
      ...prev,
      is_default_schedule: isDefault,
      from_date: isDefault ? null : dayjs().format('YYYY-MM-DD'),
      to_date: isDefault ? null : dayjs().add(1, 'year').format('YYYY-MM-DD')
    }));
  };

  const handleAddTimeSlot = (dayIndex) => {
    setScheduleForm(prev => {
      const daySchedule = Array.isArray(prev.schedule[dayIndex]) ? [...prev.schedule[dayIndex]] : [];
      daySchedule.push({ from: "09:00", to: "17:00" });
      return {
        ...prev,
        schedule: { ...prev.schedule, [dayIndex]: daySchedule }
      };
    });
  };

  const handleRemoveTimeSlot = (dayIndex, slotIndex) => {
    setScheduleForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [dayIndex]: prev.schedule[dayIndex].filter((_, i) => i !== slotIndex)
      }
    }));
  };

  const handleTimeSlotChange = (dayIndex, slotIndex, field, value) => {
    setScheduleForm(prev => {
      const updated = [...prev.schedule[dayIndex]];
      updated[slotIndex] = { ...updated[slotIndex], [field]: value };
      return { ...prev, schedule: { ...prev.schedule, [dayIndex]: updated } };
    });
  };

  const handleCopyDaySchedule = (fromDay, toDay) => {
    setScheduleForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [toDay]: [...(prev.schedule[fromDay] || [])].map(s => ({ ...s }))
      }
    }));
  };

  const handleApplyToAllDays = (fromDay) => {
    setScheduleForm(prev => {
      const sourceDaySlots = [...(prev.schedule[fromDay] || [])].map(s => ({ ...s }));
      const newSchedule = { ...prev.schedule };
      for (const day of Object.keys(newSchedule)) {
        if (day !== fromDay) newSchedule[day] = [...sourceDaySlots];
      }
      return { ...prev, schedule: newSchedule };
    });
  };

  const handleSortAllDays = () => {
    setScheduleForm(prev => {
      const newSchedule = { ...prev.schedule };
      for (const dayIndex of Object.keys(newSchedule)) {
        if (newSchedule[dayIndex]?.length > 1) {
          newSchedule[dayIndex] = [...newSchedule[dayIndex]].sort((a, b) => a.from.localeCompare(b.from));
        }
      }
      return { ...prev, schedule: newSchedule };
    });
  };

  const validateSchedule = () => {
    if (!scheduleForm.is_default_schedule) {
      if (!scheduleForm.from_date || !scheduleForm.to_date) {
        setError('From and To dates are required when not a default schedule');
        return false;
      }
      if (dayjs(scheduleForm.from_date).isAfter(dayjs(scheduleForm.to_date))) {
        setError('To date must be after From date');
        return false;
      }
    }
    for (const dayIndex of Object.keys(scheduleForm.schedule)) {
      const daySlots = scheduleForm.schedule[dayIndex];
      const dayName = weekDays.find(d => d.value === dayIndex)?.label || dayIndex;
      for (let i = 0; i < daySlots.length; i++) {
        const slot = daySlots[i];
        if (!slot.from || !slot.to) {
          setError(`Missing time values in ${dayName}`);
          return false;
        }
        if (slot.from >= slot.to) {
          setError(`End time must be after start time in ${dayName}`);
          return false;
        }
      }
      if (daySlots.length > 1) {
        const sortedSlots = [...daySlots].sort((a, b) => a.from.localeCompare(b.from));
        for (let i = 0; i < sortedSlots.length - 1; i++) {
          if (sortedSlots[i].to > sortedSlots[i + 1].from) {
            setError(`Overlapping time slots in ${dayName}`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateSchedule()) return;
    setError('');
    let submissionForm = { ...scheduleForm };
    if (submissionForm.is_default_schedule) {
      submissionForm.from_date = null;
      submissionForm.to_date = null;
    }
    const sortedSchedule = Object.keys(submissionForm.schedule).reduce((acc, dayIndex) => {
      acc[dayIndex] = [...(submissionForm.schedule[dayIndex] || [])].sort((a, b) => a.from.localeCompare(b.from));
      return acc;
    }, {});
    onNext({ scheduleForm: { ...submissionForm, schedule: sortedSchedule } });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 2: Add New Schedule</h2>
        <p className="text-gray-600">
          Configure the schedule to add. This schedule will be applied to selected doctors in the next step.
        </p>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date {!scheduleForm.is_default_schedule && <span className="text-red-500">*</span>}
            </label>
            <input
              type="date"
              name="from_date"
              value={scheduleForm.from_date || ''}
              onChange={handleFormInputChange}
              disabled={scheduleForm.is_default_schedule}
              className={`w-full p-2 border rounded ${scheduleForm.is_default_schedule ? 'bg-gray-100' : ''}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date {!scheduleForm.is_default_schedule && <span className="text-red-500">*</span>}
            </label>
            <input
              type="date"
              name="to_date"
              value={scheduleForm.to_date || ''}
              onChange={handleFormInputChange}
              disabled={scheduleForm.is_default_schedule}
              className={`w-full p-2 border rounded ${scheduleForm.is_default_schedule ? 'bg-gray-100' : ''}`}
            />
          </div>
        </div>

        <div className="flex items-start space-x-4">
          <input
            id="is_default_schedule"
            name="is_default_schedule"
            type="checkbox"
            checked={scheduleForm.is_default_schedule}
            onChange={handleDefaultScheduleChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded mt-1"
          />
          <div>
            <label htmlFor="is_default_schedule" className="font-medium text-gray-700">Default Schedule</label>
            <p className="text-sm text-gray-500">No date restrictions. Only one default per doctor.</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <input
            id="active"
            name="active"
            type="checkbox"
            checked={scheduleForm.active}
            onChange={handleFormInputChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <label htmlFor="active" className="font-medium text-gray-700">Active</label>
        </div>

        <div>
          <h5 className="text-md font-medium mb-4">Weekly Schedule</h5>
          <p className="text-sm text-gray-500 mb-2">
            Empty schedule (no slots) = delete matching schedule for selected doctors.
          </p>
          <div className="flex justify-end gap-2 mb-3">
            <button
              type="button"
              onClick={() => setScheduleForm((prev) => ({
                ...prev,
                schedule: Object.fromEntries(Object.keys(prev.schedule || {}).map((k) => [k, []]))
              }))}
              className="px-3 py-1 bg-amber-100 text-amber-800 text-xs rounded hover:bg-amber-200"
            >
              Clear All (Delete Mode)
            </button>
            <button
              type="button"
              onClick={handleSortAllDays}
              className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
            >
              Sort All Days
            </button>
          </div>
          <div className="space-y-4">
            {weekDays.map((day) => (
              <div key={day.value} className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-3 font-medium border-b flex justify-between items-center">
                  <span>{day.label}</span>
                  <div className="flex gap-2">
                    <select
                      onChange={(e) => e.target.value && handleCopyDaySchedule(e.target.value, day.value)}
                      className="bg-white border rounded px-2 py-1 text-xs"
                      defaultValue=""
                    >
                      <option value="">Copy from...</option>
                      {weekDays.filter(d => d.value !== day.value).map(d => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => handleApplyToAllDays(day.value)}
                      className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                    >
                      Apply to All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddTimeSlot(day.value)}
                      className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                    >
                      Add Slot
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  {scheduleForm.schedule[day.value]?.length === 0 ? (
                    <p className="text-gray-500 text-sm py-2">No time slots</p>
                  ) : (
                    <div className="space-y-3">
                      {overlappingSlots[day.value] && (
                        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs">
                          Overlapping slots detected
                        </div>
                      )}
                      {scheduleForm.schedule[day.value]?.map((slot, slotIndex) => (
                        <div key={slotIndex} className="flex items-center gap-3">
                          <div className="w-1/3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Start</label>
                            <input
                              type="time"
                              value={slot.from}
                              onChange={(e) => handleTimeSlotChange(day.value, slotIndex, 'from', e.target.value)}
                              className="w-full p-2 border rounded"
                            />
                          </div>
                          <div className="w-1/3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">End</label>
                            <input
                              type="time"
                              value={slot.to}
                              onChange={(e) => handleTimeSlotChange(day.value, slotIndex, 'to', e.target.value)}
                              className="w-full p-2 border rounded"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveTimeSlot(day.value, slotIndex)}
                            className="p-2 text-red-500 hover:text-red-700"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Step2_AddSchedule;
